import styles from './styles.module.css';
import React, { useState, useContext, useEffect } from 'react';
import { AlertMessagesContext } from 'react-alert-messages';
import DataTable from 'components/utils/DataTable/index';
import { Button, Tooltip, Grid } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { UserContext } from 'Context/UserContext';
import { ShopContext } from 'Context/ShopContext';
import { SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Info from 'components/utils/Alert/Info';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import ExpenseTypeService from 'services/ExpenseTypeService';
import ExpenseTypeFormDialog from 'components/popups/ExpenseTypeFormDialog';
import ExpenseTypeEditDialog from 'components/popups/ExpenseTypeEditDialog';
import { useTranslation } from 'react-i18next';

function ExpenseType() {
  const { t: tCommon } = useTranslation('common');
  const { t: tManagement } = useTranslation('management');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { user } = useContext(UserContext);
  const { shop } = useContext(ShopContext);

  const [expenseTypes, setExpenseTypes] = useState(null);
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const subscriptionType = shop?.subscription?.type;
  const userHasExpenseWritePermission = user?.shop_permissions?.expense_permission === USER_PERMISSIONS.WRITE;

  const fetchExpenseTypes = async () => {
    setLoadingIndicator(true);
    try {
      const data = await ExpenseTypeService.getExpenseTypes();
      setExpenseTypes(data);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleEditClick = (item) => setSelectedForEdit(item);
  const handleAddClick = () => setShowAddForm(true);
  const handleDeleteClick = (item) => setSelectedForDelete(item);

  const handleDelete = async (expenseType) => {
    setLoadingIndicator(true);
    try {
      await ExpenseTypeService.deleteExpenseType(expenseType.id);
      setExpenseTypes((prev) => prev.filter((c) => c.id !== expenseType.id));
      postAlertMessage({ text: tManagement('Expense type deleted successfully'), type: 'success' });
      setSelectedForDelete(null);
    } catch (error) {
      postAlertMessage({ text: error?.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const headerData = [
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Actions'),
      id: 'toggleBtn',
      type: 'callback',
      align: 'center',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroupCenter}>
            <>
              <Tooltip title={tCommon('Edit Expense Type')} onClick={() => handleEditClick(item)}>
                <EditIcon className={styles.iconButton} />
              </Tooltip>
              <Tooltip title={tCommon('Delete Expense Type')} onClick={() => handleDeleteClick(item)}>
                <DeleteIcon className={styles.iconButton} />
              </Tooltip>
            </>
          </Grid>
        );
      },
    },
  ];

  useEffect(() => {
    fetchExpenseTypes();
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <div className={styles.contentLayout}>
        <div className={styles.settingsGrid}>
          <div className={styles.BillHeaderDiv}>
            <span className={styles.pageHead}>{tCommon('Manage Expense Types')}</span>
            <div className={styles.filerInputSec}>
              {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM && userHasExpenseWritePermission && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  style={{ backgroundColor: '#ff851b' }}
                  className={styles.actionBtn}
                  onClick={handleAddClick}
                >
                  <NoteAddIcon className={styles.actionBtnIcon} />
                  {tCommon('New Expense Type')}
                </Button>
              )}
            </div>
          </div>
          {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM ? (
            <RestrictionInfo
              title={tManagement('Expense Type feature not available')}
              content={tManagement('To get Expense Type support options, contact support team.')}
            />
          ) : (
            expenseTypes &&
            (expenseTypes.length ? (
              <DataTable columns={headerData} rows={expenseTypes} />
            ) : (
              !loadingIndicator && (
                <Info
                  title={tManagement('No expense types to list')}
                  content={tManagement('You have no expense types to list. Please create a new expense type')}
                />
              )
            ))
          )}
        </div>
      </div>
      {showAddForm && (
        <ExpenseTypeFormDialog
          handleClose={() => setShowAddForm(false)}
          handleSuccess={(newType) => {
            setExpenseTypes((prev) => [...prev, newType]);
            setShowAddForm(false);
          }}
        />
      )}
      {selectedForDelete && (
        <ConfirmPopup
          data={selectedForDelete}
          handleClose={() => setSelectedForDelete(null)}
          handleOkay={() => handleDelete(selectedForDelete)}
          alertTitle={tManagement('Confirm delete')}
          successBtnName={tCommon('Delete')}
          alertContent={tManagement('Deleted expense type can\'t be restored. Do you want to proceed?')}
          isLoading={loadingIndicator}
        />
      )}
      {selectedForEdit && (
        <ExpenseTypeEditDialog
          expenseType={selectedForEdit}
          handleClose={() => {
            setSelectedForEdit(null);
          }}
          handleSuccess={(updatedType) => {
            setSelectedForEdit(null);
            const _types = expenseTypes.map((type) => (type.id === selectedForEdit.id ? updatedType : type));
            setExpenseTypes(_types);
          }}
        />
      )}
    </>
  );
}

export default ExpenseType;
